import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, X } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";

// Selector de idioma con bandera. Traduce toda la página/plataforma al cambiar.
// data-no-translate evita que el traductor mueva los nombres de idiomas.
//
// El dropdown SIEMPRE se renderiza vía portal hacia <body> anclado a la
// posición del botón en el viewport: así nunca se recorta dentro de modales
// con overflow/stacking propio (rastreadores, diálogos) y funciona igual en
// navegador móvil y app Android. Touch targets >= 44px (directrices Play).
//
// La selección se persiste en localStorage y se comparte entre landing y panel
// (mismo LanguageProvider). Un listener "storage" sincroniza entre pestañas.
function FlagImg({ id, label, emoji, className = "w-7 h-5" }) {
  const [err, setErr] = useState(false);
  const cc = id.split("-")[0].toLowerCase();
  if (err && emoji) return <span className="text-lg leading-none">{emoji}</span>;
  return (
    <img src={`https://flagcdn.com/w80/${cc}.png`} alt={label}
      onError={() => setErr(true)}
      className={`object-cover border border-ink-border/40 shrink-0 ${className}`} />
  );
}

export default function LanguageSelector({ className = "", compact = false, iconClassName }) {
  const { langId, setLang, langs } = useLanguage();
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 1023px)").matches : false
  );
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const current = langs.find((l) => l.id === langId) || langs[0];

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setIsMobile(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // Calcula la posición del panel relativa al viewport (no al contenedor),
  // para que no se recorte dentro de modales con overflow.
  useLayoutEffect(() => {
    if (!open || isMobile) return;
    const PANEL_W = 208;
    const gap = 8;
    const update = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Anclar el borde izquierdo del panel al borde izquierdo del botón:
      // el menú se expande hacia la DERECHA y hacia abajo desde la bandera.
      let left = r.left;
      // Solo si rebasa el borde derecho del viewport, recorrerlo lo mínimo para
      // mantenerlo visible (sin voltear la apertura hacia la izquierda).
      if (left + PANEL_W > vw - 8) left = Math.max(8, vw - 8 - PANEL_W);
      if (left < 8) left = 8;
      const itemH = 44;
      const panelH = Math.min(288, langs.length * itemH + 8);
      let top = r.bottom + gap;
      if (top + panelH > vh - 8) top = Math.max(8, r.top - panelH - gap);
      setPos({ left, top });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, isMobile, langs.length]);

  const choose = (id) => { setLang(id); setOpen(false); };

  const trigger = (
    <button
      ref={btnRef}
      onClick={() => setOpen((o) => !o)}
      className={`flex items-center justify-center ${compact ? "px-1.5 py-1.5" : "px-2 py-1.5"} ${iconClassName ?? "text-ink-muted"} hover:text-cyan transition-colors shrink-0`}
      title={`Idioma: ${current.label}`}
      aria-haspopup="listbox"
      aria-expanded={open}
    >
      <FlagImg id={current.id} label={current.label} emoji={current.flag} className="w-6 h-4" />
    </button>
  );

  return (
    <div className={`relative ${className}`} data-no-translate="true">
      {trigger}

      {/* Móvil: modal centrado vía portal (mejor usabilidad táctil). */}
      {open && isMobile && createPortal(
        <div data-lang-dropdown className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/80 backdrop-blur-sm" onClick={() => setOpen(false)} data-no-translate="true">
          <div className="relative w-full max-w-sm surface shadow-2xl max-h-[85dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-ink-border">
              <h3 className="font-display font-bold text-sm uppercase tracking-wide">Selecciona tu idioma</h3>
              <button onClick={() => setOpen(false)} className="text-ink-muted hover:text-ink-text min-h-[44px] min-w-[44px] flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto overscroll-contain p-1.5">
              {langs.map((l) => (
                <button
                  key={l.id}
                  onClick={() => choose(l.id)}
                  className={`w-full flex items-center gap-3 px-3 min-h-[48px] py-2.5 text-sm hover:bg-cyan/5 transition-colors ${l.id === langId ? "text-cyan bg-cyan/5" : "text-ink-muted hover:text-cyan"}`}
                >
                  <FlagImg id={l.id} label={l.label} emoji={l.flag} className="w-8 h-6" />
                  <span className="flex-1 text-left">{l.label}</span>
                  {l.id === langId && <Check className="w-4 h-4 text-cyan shrink-0" />}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Escritorio: dropdown portaled a <body>, anclado al viewport del botón.
          No se recorta dentro de modales con overflow. */}
      {open && !isMobile && createPortal(
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)} />
          <div
            data-lang-dropdown
            style={{ position: "fixed", left: pos?.left ?? 0, top: pos?.top ?? 0 }}
            className="w-52 max-h-72 overflow-y-auto overscroll-contain surface z-[95] p-1 shadow-2xl"
            role="listbox"
            data-no-translate="true"
          >
            {langs.map((l) => (
              <button
                key={l.id}
                onClick={() => choose(l.id)}
                className={`w-full flex items-center gap-2 px-2.5 min-h-[44px] py-2 text-xs hover:bg-cyan/5 transition-colors ${l.id === langId ? "text-cyan" : "text-ink-muted hover:text-cyan"}`}
                role="option"
                aria-selected={l.id === langId}
              >
                <FlagImg id={l.id} label={l.label} className="w-7 h-5" />
                <span className="flex-1 text-left">{l.label}</span>
                {l.id === langId && <Check className="w-3.5 h-3.5 text-cyan shrink-0" />}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}